import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import NewsListItem from "@/components/feature/NewsListItem";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Home() {
  useDocumentMeta({
    title: "八戒影视-最新电影电视剧资讯推荐平台",
    description:
      "八戒影视为您提供最新电影电视剧资讯和丰富影视资源，涵盖各类热门剧集与影片，满足用户多元化观影需求。",
    keywords: "八戒影视,电影,电视剧,影视资讯,影视推荐,最新电影,热门电视剧",
  });

  const featured = movies.slice(0, 5);
  const hotMovies = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const latestNews = newsList.slice(0, 5);

  return (
    <>
      <HeroCarousel movies={featured} />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* 分类快捷入口 */}
        <section className="py-10 md:py-12">
          <SectionHeading title="影视分类" subtitle="按类型快速找到你想看的影视内容" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={cat.key === "影评" ? "/news/影评" : `/category/${cat.key}`}
                className="group flex flex-col items-center text-center gap-3 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <span className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-2xl md:text-3xl`}></i>
                </span>
                <span className="text-sm font-bold text-foreground-900">{cat.label}</span>
                <span className="text-[11px] text-foreground-500 leading-snug hidden md:block">{cat.desc}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* 主内容：热门推荐 + 最新资讯侧栏 */}
        <section className="pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <SectionHeading
                title="热门影视推荐"
                subtitle="高分口碑与正在热播的影视作品"
                moreTo="/category/电影"
                moreLabel="浏览全部"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <SectionHeading
                title="最新资讯"
                subtitle="影视新闻与深度影评"
                moreTo="/news/行业新闻"
                moreLabel="全部资讯"
              />
              <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                {latestNews.map((item) => (
                  <NewsListItem key={item.id} item={item} showImage={false} />
                ))}
              </div>
              <Link
                to="/news/行业新闻"
                className="sm:hidden mt-4 flex items-center justify-center gap-1 text-sm text-primary-600 py-2.5 rounded-md border border-primary-200 cursor-pointer"
              >
                查看全部资讯
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </aside>
          </div>
        </section>

        {/* 精选推荐带 */}
        <section className="py-12 md:py-16">
          <div className="rounded-xl bg-secondary-950 px-6 py-10 md:px-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-800 text-accent-400 text-xs mb-4">
                <i className="ri-award-line"></i>
                编辑精选
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-background-50 leading-tight">
                本期主题：<br className="hidden md:block" />那些值得二刷的悬疑佳作
              </h2>
              <p className="text-sm md:text-base text-background-200/80 mt-4 leading-relaxed max-w-lg">
                从古装奇案到都市暗流，悬疑类型总能用层层递进的谜题抓住观众。我们为你整理了平台内最受欢迎的悬疑作品，
                附带剧情简介与看点解析，帮你告别选择困难。
              </p>
              <Link
                to="/category/电影"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                进入专题
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {movies.slice(2, 5).map((movie) => (
                <Link key={movie.id} to={`/detail/${movie.id}`} className="group cursor-pointer">
                  <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-secondary-900">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - 八戒影视`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="text-xs text-background-100 mt-2 truncate group-hover:text-accent-400 transition-colors">
                    {movie.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}